/**
 * SF-4845: client for the sf-api environment copy endpoints (all need eng:env-copy).
 *
 *   GET  /admin/env-copy/config          sources, targets, which targets are set up, this env
 *   POST /admin/env-copy/runs            { source, target, confirm, copyUsage, deleteExtras,
 *                                          copyPermissions, copyImages, copyReports,
 *                                          filesMonths }
 *   GET  /admin/env-copy/estimate?source&target  SF-5084: images and reports folder sizes on
 *                                          both sides (daily storage totals); an older API
 *                                          answers 404, read as "not available"
 *   GET  /admin/env-copy/runs?limit=20   newest first
 *   GET  /admin/env-copy/runs/:id        { run }: one run with its steps
 *   POST /admin/env-copy/runs/:id/resume { run }: start a run that needs resuming again
 *
 * The data provider's `custom` unwraps sf-api's `{ success, data }`, so hooks expose the payload
 * at `query.data?.data`; for one run that payload is `{ run }`, read by useEnvCopyRun.
 */
import { useCustom, useCustomMutation, useDataProvider, useOnError } from '@refinedev/core';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { AxiosError, CanceledError } from 'axios';
import {
  settledEditScope,
  useEditableCompanies,
} from '../../../companies/api/editableCompaniesClient';
import {
  ENV_COPY_LIST_LIMIT,
  ENV_COPY_POLL_MS,
  ENV_COPY_READ_TIMEOUT_MS,
  isLiveStatus,
  type RunStatus,
  type StepState,
} from '../constants';

export const ENV_COPY_BASE = 'admin/env-copy';

export interface EnvCopyConfig {
  sources: string[];
  targets: string[];
  available: Record<string, boolean>;
  currentEnv: string | null;
}

export type FlatValues = Record<string, number | string>;

export interface EnvCopyStep {
  id: string;
  label: string;
  state: StepState;
  started_at: string | null;
  ended_at: string | null;
  counts: FlatValues | null;
  progress: FlatValues | null;
  error: string | null;
}

export interface EnvCopyRun {
  run_id: string;
  source: string;
  target: string;
  /** copyPermissions is missing on runs started before the option existed; those copied the
   * source's admin app accounts. */
  options: {
    copyUsage?: boolean;
    deleteExtras?: boolean;
    copyPermissions?: boolean;
    /** SF-5084: copyImages and copyReports are missing on runs started before the choice
     * existed; those copied every image and report file. filesMonths is null for no limit. */
    copyImages?: boolean;
    copyReports?: boolean;
    filesMonths?: number | null;
  };
  started_by: { email?: string; name?: string } | null;
  status: RunStatus;
  created_at: string | null;
  started_at: string | null;
  ended_at: string | null;
  error: string | null;
  task_arn?: string | null;
  /** The runner holding the run (none once it stopped), and when it last said it was alive. A
   * runner sets both when it claims the run and refreshes heartbeat_at while it works, so a
   * changed heartbeat_at means some runner was alive since the last read, not a new attempt. */
  executor?: string | null;
  heartbeat_at?: string | null;
  saved?: Record<string, unknown>;
  steps: EnvCopyStep[];
}

export interface StartEnvCopyBody {
  source: string;
  target: string;
  confirm: string;
  copyUsage: boolean;
  deleteExtras: boolean;
  copyPermissions: boolean;
  copyImages: boolean;
  copyReports: boolean;
  /** Only images and report files changed in the last 1 to 12 months; null for all history. */
  filesMonths: number | null;
}

/** SF-5084: one side of a folder's size; each number is null when there is no figure. */
export interface EnvCopySize {
  objects: number | null;
  bytes: number | null;
}

export type EnvCopyFileKind = 'images' | 'reports';

export interface EnvCopyKindEstimate {
  kind: EnvCopyFileKind;
  source: EnvCopySize;
  target: EnvCopySize;
  /** max(0, source - target) per field; null when either side has no figure. */
  difference: EnvCopySize;
}

export interface EnvCopyEstimate {
  /** false when the storage totals could not be read; kinds is then empty. */
  available: boolean;
  measuredAt: string | null;
  kinds: EnvCopyKindEstimate[];
}

const quiet = { errorNotification: false as const, successNotification: false as const };

export const envCopyRunsQueryKey = ['sf-admin', 'env-copy', 'runs'] as const;
export const envCopyRunQueryKey = (id: string) => ['sf-admin', 'env-copy', 'run', id];

export function useEnvCopyConfig(enabled: boolean) {
  return useCustom<EnvCopyConfig>({
    url: `/${ENV_COPY_BASE}/config`,
    method: 'get',
    queryOptions: {
      queryKey: ['sf-admin', 'env-copy', 'config'],
      enabled,
      keepPreviousData: false,
      refetchOnWindowFocus: false,
    },
    errorNotification: false,
  });
}

export function useEnvCopyRuns(enabled: boolean) {
  return useCustom<{ runs: EnvCopyRun[] }>({
    url: `/${ENV_COPY_BASE}/runs`,
    method: 'get',
    config: { query: { limit: ENV_COPY_LIST_LIMIT } },
    queryOptions: {
      queryKey: [...envCopyRunsQueryKey],
      enabled,
      keepPreviousData: false,
      refetchOnWindowFocus: false,
    },
    errorNotification: false,
  });
}

/**
 * SF-5084: the images and reports folder sizes for a source and target, read only when both are
 * chosen and different. Any failure (a 404 from an older API included) means "not available";
 * the copy can be started either way, so it is not retried.
 */
export function useEnvCopyEstimate(source: string | undefined, target: string | undefined) {
  return useCustom<EnvCopyEstimate>({
    url: `/${ENV_COPY_BASE}/estimate`,
    method: 'get',
    config: { query: { source, target } },
    queryOptions: {
      queryKey: ['sf-admin', 'env-copy', 'estimate', source, target],
      enabled: !!source && !!target && source !== target,
      retry: false,
      keepPreviousData: false,
      refetchOnWindowFocus: false,
    },
    errorNotification: false,
  });
}

/**
 * What a read of one run answered: the run, or an authoritative refusal (403 or 404). A refusal
 * is the query's DATA, not its error, so it replaces the cached run and a later failed read
 * (which keeps the last data) can never bring the refused run back.
 */
export type EnvCopyRunAnswer =
  | { kind: 'run'; run: EnvCopyRun }
  | { kind: 'refused'; status: 403 | 404; message: string };

/**
 * Read again every few seconds while the cached run is queued or running, never in a hidden tab.
 * A run that needs resuming is read again only while the page waits for an accepted resume to be
 * picked up (`followRecovery`); the page bounds that wait.
 */
export const runRefetchInterval = (
  answer: EnvCopyRunAnswer | undefined,
  followRecovery: boolean,
) => {
  if (answer?.kind !== 'run') return false;
  const { status } = answer.run;
  if (isLiveStatus(status)) return ENV_COPY_POLL_MS;
  return followRecovery && status === 'recovery_required' ? ENV_COPY_POLL_MS : false;
};

/**
 * Settle with `work`, or with a timeout after `ms`, or as soon as `signal` aborts, whichever is
 * first. Covers every wait inside `work`, such as the shared client's sign-in refresh after a
 * 401, which has no deadline of its own. `work` itself is not cancelled: a refresh is shared
 * with other requests, so only this caller stops waiting for it.
 */
export function settleWithin<T>(work: Promise<T>, ms: number, signal?: AbortSignal): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const finish = (end: () => void) => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      end();
    };
    const onAbort = () => finish(() => reject(new CanceledError()));
    const timer = setTimeout(
      () =>
        finish(() =>
          reject(new AxiosError(`timeout of ${ms}ms exceeded`, AxiosError.ECONNABORTED)),
        ),
      ms,
    );
    // Handlers go on first, so a rejection of `work` is always observed, even when the signal
    // has already aborted (a settled promise ignores the later resolve/reject).
    work.then(
      (value) => finish(() => resolve(value)),
      (error) => finish(() => reject(error)),
    );
    if (signal?.aborted) {
      onAbort();
      return;
    }
    signal?.addEventListener('abort', onAbort);
  });
}

/**
 * One run. The query key carries the id, so a poll belongs to the run it started for. Each read,
 * any sign-in refresh it waits for included, ends within the deadline or when the query's signal
 * aborts: moving to another run, or closing the page, ends the read in flight as well as the timer.
 */
export function useEnvCopyRun(id: string, enabled: boolean, followRecovery = false) {
  const getDataProvider = useDataProvider();
  // useCustom hands failures to the auth provider (a 401 that cannot be refreshed logs out).
  const { mutate: checkError } = useOnError();
  return useQuery<EnvCopyRunAnswer>({
    queryKey: envCopyRunQueryKey(id),
    queryFn: async ({ signal }) => {
      const custom = getDataProvider().custom;
      if (!custom) throw new Error('The data provider has no custom request.');
      try {
        const { data } = await settleWithin(
          custom<{ run?: EnvCopyRun }>({
            url: `/${ENV_COPY_BASE}/runs/${encodeURIComponent(id)}`,
            method: 'get',
            meta: { signal, timeout: ENV_COPY_READ_TIMEOUT_MS },
          }),
          ENV_COPY_READ_TIMEOUT_MS,
          signal,
        );
        const run = data?.run;
        if (!run || run.run_id !== id) {
          throw new Error('The copy answer was not in the expected shape.');
        }
        return { kind: 'run', run };
      } catch (error) {
        const status = errorStatus(error);
        if (status !== 403 && status !== 404) throw error;
        const fallback = status === 404 ? 'This copy was not found.' : 'You may not see this copy.';
        return { kind: 'refused', status, message: envCopyErrorMessage(error, fallback) };
      }
    },
    enabled: enabled && !!id,
    retry: false,
    keepPreviousData: false,
    refetchInterval: (answer) => runRefetchInterval(answer, followRecovery),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    onError: (error) => checkError(error),
  });
}

/**
 * Put a run the server just returned (start, resume) into its read's cache, cancelling any read
 * begun before so it cannot land after and put the older copy back.
 */
export async function putEnvCopyRun(queryClient: QueryClient, run: EnvCopyRun) {
  const queryKey = envCopyRunQueryKey(run.run_id);
  await queryClient.cancelQueries({ queryKey });
  queryClient.setQueryData<EnvCopyRunAnswer>(queryKey, { kind: 'run', run });
}

/**
 * May the caller start or resume a copy? sf-api allows both only for staff who may edit the whole
 * platform. Fails closed: no while the scope is being read (again) or the read failed.
 */
export function useCopyStartScope() {
  const query = useEditableCompanies();
  const scope = settledEditScope(query);
  return {
    canStart: scope?.all === true,
    checking: query.isLoading || query.isFetching,
    failed: !query.isFetching && query.isError,
    refetch: query.refetch,
  };
}
export type CopyStartScope = ReturnType<typeof useCopyStartScope>;

export const useEnvCopyMutation = useCustomMutation;

export const startEnvCopyArgs = (values: StartEnvCopyBody) => ({
  url: `/${ENV_COPY_BASE}/runs`,
  method: 'post' as const,
  values,
  ...quiet,
});

export const resumeEnvCopyArgs = (runId: string) => ({
  url: `/${ENV_COPY_BASE}/runs/${encodeURIComponent(runId)}/resume`,
  method: 'post' as const,
  values: {},
  ...quiet,
});

type ServerError = AxiosError<{ message?: string; data?: { runId?: string } }> & {
  statusCode?: number;
};

/** HTTP status of a failed call (axios error or Refine's HttpError). */
export function errorStatus(error: unknown): number | undefined {
  const err = error as ServerError | undefined;
  return err?.response?.status ?? err?.statusCode;
}

/** The server's own message, or the fallback. */
export function envCopyErrorMessage(error: unknown, fallback: string): string {
  const err = error as ServerError | undefined;
  return err?.response?.data?.message || fallback;
}

/** The running copy's id on a 409 (target already busy). */
export function runningRunId(error: unknown): string | undefined {
  const err = error as ServerError | undefined;
  return err?.response?.data?.data?.runId;
}
